import { exists, isNumber, isPartialDate, isTime, partialDateToValue } from 'neuro-utils';
import { fromPairs } from 'ramda';
import { isBoolean, isString } from 'Utility/typeGuards';
import { genericOptionValues } from './configs';

type TSimpleData = string | number | boolean | undefined | null;

export const isSimpleObject = (data: unknown): data is Record<string, string | number | boolean | undefined | null> => {
  return !!(
    data &&
    typeof data === 'object' &&
    Object.keys(data).every(key => {
      const value = (data as Record<string, TFieldValue>)[key];
      return isString(value) || isNumber(value) || isBoolean(value) || !exists(value);
    })
  );
};

const returnSortDateValue = (object: Record<string, unknown>): number | null => {
  // date, startDate or _cdate
  const sortValue =
    'date' in object && isPartialDate(object['date'])
      ? partialDateToValue(object['date']).valueOf()
      : 'startDate' in object && isPartialDate(object['startDate'])
        ? partialDateToValue(object['startDate']).valueOf()
        : '_cdate' in object && typeof object['_cdate'] === 'number'
          ? object['_cdate']
          : null;
  return sortValue;
};

const isStringArray = (arr: unknown[]): arr is string[] => {
  return arr.every(a => typeof a === 'string');
};
const isObjectArray = (arr: unknown[]): arr is Record<string, TFieldValue>[] => {
  return arr.every(a => {
    if (typeof a === 'object' && a !== null && Object.keys(a).every(key => typeof key === 'string')) {
      // Not complete
      return true;
    }
    return false;
  });
};

export const returnSimpleObjectArray = (
  arr: unknown[],
  fieldNameLocaleMap: Record<string, string | null>,
  valueLocaleMap: Record<string, Record<string, string> | ((value: string) => string) | undefined>,
): Array<Record<string, TSimpleData | PartialDate | string[]>> => {
  if (!isObjectArray(arr)) return [];
  const sortedArray = arr.toSorted((a, b) => {
    const sortFieldA: number | null = returnSortDateValue(a);
    const sortFieldB: number | null = returnSortDateValue(b);
    return (sortFieldB ?? 0) - (sortFieldA ?? 0);
  });

  const mappedValuesArray = sortedArray.map(sa => {
    const keyPairs = Object.keys(sa)
      .map(key => {
        // Try to map field names to something localized
        const localeKey: string | null | undefined = ['date', 'startDate'].includes(key)
          ? 'general.date'
          : ['endDate'].includes(key)
            ? 'general.endDate'
            : key in fieldNameLocaleMap
              ? fieldNameLocaleMap[key]
              : key;

        const valueMapThisKey = valueLocaleMap[key];
        const thisValue = sa[key];
        const value: string | string[] | PartialDate | number | Time = isPartialDate(thisValue)
          ? thisValue
          : isTime(thisValue)
            ? thisValue
            : !exists(thisValue) || thisValue === '' || (Array.isArray(thisValue) && thisValue.length === 0)
              ? '-'
              : typeof valueMapThisKey === 'function' && typeof thisValue === 'string'
                ? valueMapThisKey(thisValue)
                : typeof thisValue === 'string' && typeof valueMapThisKey === 'object' && thisValue in valueMapThisKey
                  ? (valueMapThisKey[thisValue] ?? '-')
                  : Array.isArray(thisValue) && isStringArray(thisValue)
                    ? thisValue.map(tv => (typeof valueMapThisKey === 'function' ? valueMapThisKey(tv) : tv))
                    : genericOptionValues(thisValue)
                      ? genericOptionValues(thisValue)!
                      : typeof thisValue === 'string'
                        ? thisValue
                        : typeof thisValue === 'number'
                          ? thisValue
                          : Array.isArray(thisValue) && thisValue.length === 1 && thisValue[0] === true
                            ? 'general.yes'
                            : '-';

        return [localeKey, value];
      })
      .filter(pair => pair[0]) as [string, string | string[] | PartialDate][];

    return fromPairs(keyPairs);
  });
  return mappedValuesArray;
  //return [];
};
